import {useEffect, useRef} from 'react';
import {useLatest} from 'react-use';
import {datadogRum} from '@datadog/browser-rum';

import useTracking from '@/hooks/useTracking';
import {NAVLINK_TRACKING} from '../../appBarTracking';
import useNavbarVersion from '../../hooks/useNavbarVersion';
import {type DraftCartTrackingInput} from '../tracking/getDraftCartTrackingFields';
import summarizeDraftCarts from '../tracking/summarizeDraftCarts';

export type DraftCartDropdownState = 'list' | 'empty' | 'loading' | 'error';

type CartOpenTrackerProps = {
  area: 'desktop' | 'mobile';
  carts: readonly DraftCartTrackingInput[];
  state: DraftCartDropdownState;
  /** FOF navbar phase 2a treatment only:
   * carts per server section key. */
  sectionCounts?: Record<string, number>;
};

/**
 * Fires `appbar-cart-opened` once per open of the draft cart dropdown. The dropdown's content is
 * mounted only while it is open — the same property `NotificationsOpenTracker` relies on — so one
 * mount is one open. It waits for the first non-loading state so the counts describe what was
 * shown; an open that closes while still loading is reported on unmount as `state: loading`.
 *
 * In development, React Strict Mode's simulated unmount can report a spurious `loading` open when
 * the dropdown opens before the query has resolved. Production is unaffected.
 */
const CartOpenTracker: React.FC<CartOpenTrackerProps> = ({area, carts, state, sectionCounts}) => {
  const {track} = useTracking();
  const version = useNavbarVersion('draftCart');
  const hasFiredRef = useRef(false);

  const fireRef = useLatest((firedState: DraftCartDropdownState) => {
    hasFiredRef.current = true;

    const properties = {
      area,
      version,
      state: firedState,
      ...summarizeDraftCarts(carts),
      ...(sectionCounts && {section_counts: sectionCounts}),
    };

    track(NAVLINK_TRACKING.cart.dropdownOpened, {
      url: window.location.href,
      misc_json: JSON.stringify(properties),
    });

    // Mirrors NotificationsOpenTracker: the desktop open is the "hover" in Datadog; the mobile
    // modal's open is already a click on the cart button (navlink.cart.clicked).
    if (area === 'desktop') datadogRum.addAction(NAVLINK_TRACKING.cart.hovered, properties);
  });

  useEffect(() => {
    if (!hasFiredRef.current && state !== 'loading') fireRef.current(state);
  }, [state, fireRef]);

  useEffect(
    () => () => {
      if (!hasFiredRef.current) fireRef.current('loading');
    },
    [fireRef],
  );

  return null;
};

export default CartOpenTracker;
